import React, { useState, useEffect, useCallback } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import Swal from 'sweetalert2'
import jsPDF from 'jspdf'
import 'jspdf-autotable'

const AdjustmentAudit = () => {
  const [adjustments, setAdjustments] = useState([])
  const [filteredAdjustments, setFilteredAdjustments] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [filters, setFilters] = useState({
    search: '',
    warehouse_id: '',
    adjustment_type: '',
    date_from: '',
    date_to: ''
  })

  const applyFilters = useCallback(() => {
    let filtered = [...adjustments]

    // Search filter (product_name, product_sku, reason, created_by_email)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(adj =>
        adj.product_name?.toLowerCase().includes(searchLower) ||
        adj.product_sku?.toLowerCase().includes(searchLower) ||
        adj.reason?.toLowerCase().includes(searchLower) ||
        adj.created_by_email?.toLowerCase().includes(searchLower)
      )
    }

    // Warehouse filter
    if (filters.warehouse_id) {
      filtered = filtered.filter(adj => adj.warehouse === parseInt(filters.warehouse_id))
    }

    // Adjustment type filter
    if (filters.adjustment_type) {
      filtered = filtered.filter(adj => adj.adjustment_type === filters.adjustment_type)
    }

    // Date range filter
    if (filters.date_from) {
      const fromDate = new Date(filters.date_from)
      fromDate.setHours(0, 0, 0, 0)
      filtered = filtered.filter(adj => {
        if (!adj.created_at) return false
        const adjDate = new Date(adj.created_at)
        return adjDate >= fromDate
      })
    }

    if (filters.date_to) {
      const toDate = new Date(filters.date_to)
      toDate.setHours(23, 59, 59, 999)
      filtered = filtered.filter(adj => {
        if (!adj.created_at) return false
        const adjDate = new Date(adj.created_at)
        return adjDate <= toDate
      })
    }

    setFilteredAdjustments(filtered)
  }, [adjustments, filters])

  useEffect(() => {
    fetchWarehouses()
    fetchAdjustments()
  }, [])

  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  const fetchWarehouses = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.WAREHOUSES)
      const warehousesData = response.data.results || response.data || []
      setWarehouses(warehousesData)
    } catch (error) {
      console.error('Error fetching warehouses:', error)
    }
  }

  const fetchAdjustments = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get(API_ENDPOINTS.STOCK_ADJUSTMENTS)
      const adjustmentsData = response.data || []
      setAdjustments(adjustmentsData)
      setFilteredAdjustments(adjustmentsData)
    } catch (error) {
      console.error('Error fetching stock adjustments:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || error.response?.data?.message || 'Failed to fetch stock adjustments. Please try again.',
        confirmButtonColor: '#d33'
      })
      setAdjustments([])
      setFilteredAdjustments([])
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    })
  }

  const clearFilters = () => {
    setFilters({
      search: '',
      warehouse_id: '',
      adjustment_type: '',
      date_from: '',
      date_to: ''
    })
  }

  const toggleRowExpansion = (adjustmentId) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(adjustmentId)) {
      newExpanded.delete(adjustmentId)
    } else {
      newExpanded.add(adjustmentId)
    }
    setExpandedRows(newExpanded)
  }

  const getAdjustmentTypeBadge = (type) => {
    const badges = {
      'addition': { class: 'success', icon: 'bi-plus-circle', label: 'Addition' },
      'removal': { class: 'danger', icon: 'bi-dash-circle', label: 'Removal' },
      'damaged': { class: 'warning', icon: 'bi-exclamation-triangle', label: 'Damaged' },
      'correction': { class: 'info', icon: 'bi-pencil-square', label: 'Correction' }
    }
    const badge = badges[type] || { class: 'secondary', icon: 'bi-circle', label: type }
    return (
      <span className={`badge bg-${badge.class}`}>
        <i className={`bi ${badge.icon} me-1`}></i>
        {badge.label}
      </span>
    )
  }

  const formatCurrency = (value) => {
    if (!value) return 'N/A'
    const num = parseFloat(value)
    if (isNaN(num)) return 'N/A'
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const showAdjustmentDetails = (adjustment) => {
    const detailsHtml = `
      <div style="text-align: left; padding: 10px; background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); border-radius: 8px; margin-bottom: 10px;">
        <div style="display: flex; align-items: center; margin-bottom: 10px; padding-bottom: 10px; border-bottom: 2px solid rgba(0,0,0,0.1);">
          <div style="width: 40px; height: 40px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 16px; font-weight: bold; margin-right: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.2);">
            <i class="bi bi-clipboard-data"></i>
          </div>
          <div>
            <h3 style="margin: 0; color: #2c3e50; font-weight: 600; font-size: 16px;">${adjustment.product_name || 'N/A'}</h3>
            <p style="margin: 2px 0 0 0; color: #7f8c8d; font-size: 12px;">Adjustment ID: ${adjustment.id || 'N/A'}</p>
          </div>
        </div>
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">SKU</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${adjustment.product_sku || 'N/A'}</div>
          </div>
          
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Warehouse</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${adjustment.warehouse_name || 'N/A'}</div>
          </div>
        </div>

        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Adjustment Type</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${adjustment.adjustment_type || 'N/A'}</div>
          </div>
          
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Quantity</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${adjustment.quantity || 0}</div>
          </div>
        </div>

        <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1); margin-bottom: 8px;">
          ${adjustment.purchase_price ? `
            <div style="margin-bottom: 6px;">
              <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Purchase Price</div>
              <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${formatCurrency(adjustment.purchase_price)}</div>
            </div>
          ` : ''}
          <div style="margin-bottom: 6px;">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Reason</div>
            <div style="color: #2c3e50; font-size: 12px; white-space: pre-wrap;">${adjustment.reason || 'N/A'}</div>
          </div>
          ${adjustment.reference_number ? `
            <div style="margin-bottom: 6px;">
              <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Reference Number</div>
              <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${adjustment.reference_number}</div>
            </div>
          ` : ''}
          <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(0,0,0,0.05); font-size: 11px; color: #7f8c8d;">
            <div>Created By: <span style="color: #2c3e50;">${adjustment.created_by_email || 'N/A'}</span></div>
            <div>Created At: <span style="color: #2c3e50;">${formatDate(adjustment.created_at)}</span></div>
          </div>
        </div>
      </div>
    `

    Swal.fire({
      title: '<span style="color: #2c3e50; font-size: 18px; font-weight: 600;">Adjustment Details</span>',
      html: detailsHtml,
      icon: 'info',
      showCloseButton: true,
      confirmButtonText: 'Close',
      confirmButtonColor: '#3085d6',
      width: '600px',
      heightAuto: false,
      padding: '1rem',
      customClass: {
        popup: 'swal2-popup-custom'
      }
    })
  }

  const handleExportPdf = () => {
    if (filteredAdjustments.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Data',
        text: 'There are no adjustments to export.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    const doc = new jsPDF('landscape', 'mm', 'a4')
    doc.setFontSize(16)
    doc.text('Stock Adjustments Audit Report', 14, 16)
    doc.setFontSize(12)
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 24)

    doc.autoTable({
      startY: 30,
      head: [['Product', 'SKU', 'Warehouse', 'Type', 'Quantity', 'Price', 'Reason', 'Created By', 'Date']],
      body: filteredAdjustments.map(adj => [
        adj.product_name || 'N/A',
        adj.product_sku || 'N/A',
        adj.warehouse_name || 'N/A',
        adj.adjustment_type || 'N/A',
        adj.quantity || 0,
        adj.purchase_price ? `$${adj.purchase_price}` : 'N/A',
        (adj.reason || '').substring(0, 30) + (adj.reason && adj.reason.length > 30 ? '...' : ''),
        adj.created_by_email || 'N/A',
        adj.created_at ? new Date(adj.created_at).toLocaleDateString() : 'N/A'
      ]),
      styles: { fontSize: 7 },
      headStyles: { fillColor: [102, 126, 234] }
    })

    const fileName = `adjustment-audit-${new Date().toISOString().split('T')[0]}.pdf`
    doc.save(fileName)
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Adjustment Audit</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active">Adjustment Audit</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-3" style={{ 
                  paddingBottom: '15px',
                  borderBottom: '2px solid #e9ecef'
                }}>
                  <div className="d-flex align-items-center gap-4 flex-wrap">
                    <h5 className="card-title mb-0">
                      <i className="bi bi-clipboard-check me-2"></i>
                      Stock Adjustment Audit
                    </h5>
                  </div>
                  
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-sm btn-outline-danger"
                      onClick={handleExportPdf}
                      disabled={loading || filteredAdjustments.length === 0}
                      style={{
                        borderRadius: '6px',
                        padding: '6px 14px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                      title="Export to PDF"
                    >
                      <i className="bi bi-file-earmark-pdf me-1"></i>
                      PDF
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={fetchAdjustments}
                      disabled={loading}
                      style={{
                        borderRadius: '6px',
                        padding: '6px 14px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Refresh
                    </button>
                  </div>
                </div>
                <p className="text-muted">Review and audit all stock adjustments made in the system.</p>

                {/* Filters Section */}
                <div className="card mb-3">
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-3">
                        <label htmlFor="search" className="form-label">Search</label>
                        <input
                          type="text"
                          className="form-control"
                          id="search"
                          name="search"
                          placeholder="Product, SKU, Reason, or User"
                          value={filters.search}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="col-md-2">
                        <label htmlFor="warehouse_id" className="form-label">Warehouse</label>
                        <select
                          className="form-select"
                          id="warehouse_id"
                          name="warehouse_id"
                          value={filters.warehouse_id}
                          onChange={handleFilterChange}
                        >
                          <option value="">All Warehouses</option>
                          {warehouses.map(warehouse => (
                            <option key={warehouse.id} value={warehouse.id}>
                              {warehouse.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label htmlFor="adjustment_type" className="form-label">Type</label>
                        <select
                          className="form-select"
                          id="adjustment_type"
                          name="adjustment_type"
                          value={filters.adjustment_type}
                          onChange={handleFilterChange}
                        >
                          <option value="">All Types</option>
                          <option value="addition">Addition</option>
                          <option value="removal">Removal</option>
                          <option value="damaged">Damaged</option>
                          <option value="correction">Correction</option>
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label htmlFor="date_from" className="form-label">From Date</label>
                        <input
                          type="date"
                          className="form-control"
                          id="date_from"
                          name="date_from"
                          value={filters.date_from}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="col-md-2">
                        <label htmlFor="date_to" className="form-label">To Date</label>
                        <input
                          type="date"
                          className="form-control"
                          id="date_to"
                          name="date_to"
                          value={filters.date_to}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="col-md-1 d-flex align-items-end">
                        <button
                          className="btn btn-outline-secondary w-100"
                          onClick={clearFilters}
                          title="Clear filters"
                        >
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Adjustments Table */}
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading adjustments...</p>
                  </div>
                ) : filteredAdjustments.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                    <p className="mt-3 text-muted">No adjustments found</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '30px' }}></th>
                          <th>Product Name</th>
                          <th>Type</th>
                          <th className="text-end">Quantity</th>
                          <th>Warehouse</th>
                          <th className="text-end">Purchase Price</th>
                          <th>Created By</th>
                          <th>Date</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAdjustments.map((adjustment) => {
                          const isExpanded = expandedRows.has(adjustment.id)
                          return (
                            <React.Fragment key={adjustment.id}>
                              <tr>
                                <td>
                                  <button
                                    className="btn btn-sm btn-link p-0"
                                    onClick={() => toggleRowExpansion(adjustment.id)}
                                    style={{ border: 'none', background: 'none' }}
                                  >
                                    <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                                  </button>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="avatar-sm bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', fontSize: '14px' }}>
                                      <i className="bi bi-box"></i>
                                    </div>
                                    <div>
                                      <div className="fw-semibold">{adjustment.product_name || 'N/A'}</div>
                                      <small className="text-muted">{adjustment.product_sku || 'N/A'}</small>
                                    </div>
                                  </div>
                                </td>
                                <td>{getAdjustmentTypeBadge(adjustment.adjustment_type)}</td>
                                <td className="text-end">
                                  <span className={`fw-semibold ${parseFloat(adjustment.quantity) >= 0 ? 'text-success' : 'text-danger'}`}>
                                    {parseFloat(adjustment.quantity) >= 0 ? '+' : ''}{parseFloat(adjustment.quantity || 0).toLocaleString()}
                                  </span>
                                </td>
                                <td>{adjustment.warehouse_name || 'N/A'}</td>
                                <td className="text-end">
                                  {adjustment.purchase_price ? formatCurrency(adjustment.purchase_price) : 'N/A'}
                                </td>
                                <td>
                                  <small className="text-muted">{adjustment.created_by_email || 'N/A'}</small>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {formatDate(adjustment.created_at)}
                                  </small>
                                </td>
                                <td className="text-center">
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => showAdjustmentDetails(adjustment)}
                                    title="View Details"
                                  >
                                    <i className="bi bi-eye"></i>
                                  </button>
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr>
                                  <td colSpan="9" style={{ backgroundColor: '#f8f9fa', padding: '20px' }}>
                                    <div className="row g-3">
                                      <div className="col-md-6">
                                        <div className="card border-0 shadow-sm">
                                          <div className="card-body">
                                            <h6 className="card-title text-muted mb-3">
                                              <i className="bi bi-info-circle me-2"></i>
                                              Adjustment Details
                                            </h6>
                                            <div className="row g-3">
                                              <div className="col-6">
                                                <small className="text-muted d-block">Product ID</small>
                                                <strong>{adjustment.product || 'N/A'}</strong>
                                              </div>
                                              <div className="col-6">
                                                <small className="text-muted d-block">Adjustment ID</small>
                                                <strong>{adjustment.id || 'N/A'}</strong>
                                              </div>
                                              <div className="col-6">
                                                <small className="text-muted d-block">Warehouse ID</small>
                                                <strong>{adjustment.warehouse || 'N/A'}</strong>
                                              </div>
                                              <div className="col-6">
                                                <small className="text-muted d-block">Purchase Price</small>
                                                <strong>
                                                  {adjustment.purchase_price ? formatCurrency(adjustment.purchase_price) : 'N/A'}
                                                </strong>
                                              </div>
                                              {adjustment.reference_number && (
                                                <div className="col-12">
                                                  <small className="text-muted d-block">Reference Number</small>
                                                  <strong>{adjustment.reference_number}</strong>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-6">
                                        <div className="card border-0 shadow-sm">
                                          <div className="card-body">
                                            <h6 className="card-title text-muted mb-3">
                                              <i className="bi bi-file-text me-2"></i>
                                              Additional Information
                                            </h6>
                                            <div className="mb-3">
                                              <small className="text-muted d-block mb-2">Reason</small>
                                              <div className="p-2 bg-light rounded" style={{ minHeight: '60px' }}>
                                                {adjustment.reason || 'N/A'}
                                              </div>
                                            </div>
                                            <div className="row g-3">
                                              <div className="col-6">
                                                <small className="text-muted d-block">Created By</small>
                                                <strong>{adjustment.created_by_email || 'N/A'}</strong>
                                              </div>
                                              <div className="col-6">
                                                <small className="text-muted d-block">Created At</small>
                                                <strong>
                                                  {formatDate(adjustment.created_at)}
                                                </strong>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Results Count */}
                {!loading && (
                  <div className="mt-3 text-muted">
                    <small>
                      Showing <strong>{filteredAdjustments.length}</strong> of <strong>{adjustments.length}</strong> adjustments
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default AdjustmentAudit
